/*  ========================================================================
    # Component - Products
    ========================================================================  */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'motion/react';
import useTranslation from '../../hooks/useTranslation';

const ItemComponent = ({ children, href }) => {
  const Component = href ? 'a' : 'div';

  const handleItemClick = e => {
    if (href && href.startsWith('#')) {
      e.preventDefault();
      const overlayID = href.substring(1);
      const overlayElement = document.getElementById(overlayID);

      if (overlayElement) {
        const overlayOpenEvent = new Event('overlayOpened');
        overlayElement.showModal();
        overlayElement.dispatchEvent(overlayOpenEvent); // Dispatch custom event
      }
    }
  };

  return (
    <Component href={href} className="s-products__item" onClick={e => handleItemClick(e)}>
      {children}
    </Component>
  );
};

ItemComponent.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string
};

const Products = ({ variant, content, products, categories, initialPerPage = 9 }) => {
  const __ = useTranslation();
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeProductID, setActiveProductId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(initialPerPage || products.length);
  const productsRef = useRef(filteredProducts.map(() => React.createRef()));

  const isPaginated = initialPerPage > 0;
  const displayedProducts =
    isPaginated && activeCategory === 'all'
      ? filteredProducts.slice(0, visibleCount)
      : filteredProducts;
  const hasMore = isPaginated && activeCategory === 'all' && filteredProducts.length > visibleCount;

  const handleCategoryClick = categorySlug => {
    setActiveCategory(categorySlug);
  };

  const handleCategoryKeyDown = (event, categorySlug) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCategoryClick(categorySlug);

      if (productsRef.current && productsRef.current[0] && productsRef.current[0].current) {
        productsRef.current[0].current.focus();
      }
    }
  };

  const handleItemsKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (productsRef.current && productsRef.current[0] && productsRef.current[0].current) {
        productsRef.current[0].current.focus();
      }
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      if (event.shiftKey) return; // Ignore if Shift is also pressed

      // Focus next item
      event.preventDefault();
      const currentIndex = productsRef.current.findIndex(
        ref => ref.current === document.activeElement
      );
      const nextIndex = (currentIndex + 1) % productsRef.current.length;
      if (
        productsRef.current &&
        productsRef.current[nextIndex] &&
        productsRef.current[nextIndex].current
      ) {
        productsRef.current[nextIndex].current.focus();
      }
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      // Focus previous item
      event.preventDefault();
      const currentIndex = productsRef.current.findIndex(
        ref => ref.current === document.activeElement
      );
      const prevIndex =
        (currentIndex - 1 + productsRef.current.length) % productsRef.current.length;
      if (
        productsRef.current &&
        productsRef.current[prevIndex] &&
        productsRef.current[prevIndex].current
      ) {
        productsRef.current[prevIndex].current.focus();
      }
    }
  };

  const handleDescriptionExpandClick = ID => {
    setActiveProductId(prevID => (prevID === ID ? null : ID));
  };

  useEffect(() => {
    if (activeCategory && activeCategory !== 'all') {
      const filtered = products.filter(product => {
        return product.categories && product.categories.some(cat => cat.slug === activeCategory);
      });
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
      setVisibleCount(initialPerPage || products.length);
    }
  }, [activeCategory, initialPerPage, products]);

  return (
    <div className="container">
      <div className="s-products__wrapper">
        {content && (
          <div className="s-products__contennt">
            <div className="wysiwyg-block" dangerouslySetInnerHTML={{ __html: content }}></div>
          </div>
        )}
        {categories && Object.keys(categories).length > 0 && (
          <div className="s-products__filters">
            <button
              className={`c-button c-button--outline s-products__filter ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategoryClick('all')}
              onKeyDown={e => handleCategoryKeyDown(e, 'all')}
              aria-label={__('products-all-filter-label')}
              aria-pressed={activeCategory === 'all'}
              role="tab"
              tabIndex={0}
            >
              {__('products-all-filter-text')}
            </button>
            {Object.entries(categories).map(([key, category], index) => (
              <button
                key={index}
                className={`c-button c-button--outline s-products__filter ${activeCategory === category.slug ? 'active' : ''}`}
                onClick={() => handleCategoryClick(category.slug)}
                onKeyDown={e => handleCategoryKeyDown(e, category.slug)}
                aria-label={__('products-filter-label', { category: category.name })}
                aria-pressed={activeCategory === category.slug}
                role="tab"
                tabIndex={0}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}
        {filteredProducts && filteredProducts.length > 0 && (
          <AnimatePresence>
            <div
              className="s-products__items"
              tabIndex={0}
              role="tabpanel"
              aria-live="polite"
              onKeyDown={e => handleItemsKeyDown(e)}
            >
              {displayedProducts.map((product, index) => (
                <motion.div
                  key={product.post.ID}
                  className={`s-products__item-wrapper ${product.description ? 's-products__item-wrapper--hoverable' : ''} ${activeProductID === product.post.ID ? 'active' : ''}`}
                  layout
                  tabIndex={-1}
                  ref={productsRef.current[index]}
                >
                  <ItemComponent href={product.overlay ? `#${product.overlay}` : null}>
                    {product.description && (
                      <button
                        type="button"
                        className="s-products__item-button"
                        aria-label={__('products-open-description-label')}
                        onClick={() => handleDescriptionExpandClick(product.post.ID)}
                      ></button>
                    )}
                    <div className="s-products__item-content">
                      <p className="s-products__item-title">{product.title}</p>
                      {product.description && (
                        <div className="s-products__item-description">
                          <div
                            className="wysiwyg-block"
                            dangerouslySetInnerHTML={{ __html: product.description }}
                          ></div>
                        </div>
                      )}
                    </div>
                    <figure className="s-products__item-image">
                      <img src={product.image.sizes.medium_large} alt={product.image.alt} />
                      {product.image.caption ? (
                        <div className="u-image-caption">{product.image.caption}</div>
                      ) : (
                        ''
                      )}
                    </figure>
                  </ItemComponent>
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        )}
        {hasMore && (
          <div className="s-products__load-more">
            <button
              type="button"
              className="c-button"
              onClick={() => setVisibleCount(filteredProducts.length)}
            >
              {__('products-load-more')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

Products.propTypes = {
  variant: PropTypes.string,
  content: PropTypes.string,
  products: PropTypes.array,
  categories: PropTypes.array,
  initialPerPage: PropTypes.number
};

export default Products;
